import { Add, Edit } from '@mui/icons-material';
import { Box, Button, Stack, StackProps, Tooltip } from '@mui/material';
import {
  ExportCsv,
  GridActionsCellItem,
  GridActionsColDef,
  GridColDef,
  Toolbar,
} from '@mui/x-data-grid-pro';
import { endOfYear, startOfDay } from 'date-fns';
import _ from 'lodash';
import { FC, useMemo, useState } from 'react';

import { formatName } from '../../../components/formatUtils';
import { formatDate } from '../../../components/formatUtils/dateFormatUtils';
import { useNavigateDialogForm } from '../../../components/formikDialogForm/dialogForm.hook';
import { AppDataGrid } from '../../../components/table/AppDataGrid';
import { useAppSelector } from '../../../state/redux.hooks';
import { useMitarbeiterRole } from '../../auth/api/auth.hook';
import { selectMitarbeiterId } from '../../auth/api/authSlice';
import { MitarbeiterRole } from '../../auth/api/types';
import { MitarbeiterLink } from '../../mitarbeiter/components/MitarbeiterLink';
import { Projekt } from '../../projekt/api/projekt.types';
import { ProjektLink } from '../../projekt/components/ProjektLink';
import { AufwandApiFilters, PaginationState, useFindAllAufwandQuery } from '../api/aufwandApi';
import { Aufwand } from '../api/types';
import { DeleteAufandDialog } from '../delete/DeleteAufandDialog';

import { AufwaendeZusammenfassungDialog } from './AufwaendeZusammenfassungDialog';
import { FilterSidePanel } from './FilterSidePanel';
import { RechnungsNummerDialogButton } from './RechnungsNummerDialogButton';

const aufwandTableColumns: GridColDef<Aufwand>[] = [
  {
    field: 'id',
    headerName: 'id',
    flex: 3,
    sortable: false,
  },
  {
    field: 'taetigkeit',
    headerName: 'Tätigkeit',
    flex: 5,
    sortable: false,
    type: 'string',
  },
  {
    field: 'mitarbeiter',
    headerName: 'Mitarbeiter',
    flex: 2,
    sortable: false,
    renderCell: params => params.value && <MitarbeiterLink id={params.value.id} />,
    valueFormatter: value => formatName(value),
  },
  {
    field: 'projekt',
    headerName: 'Projekt',
    flex: 3,
    sortable: false,
    renderCell: params => params.value && <ProjektLink id={params.value.id} />,
    valueFormatter: (value: Projekt | undefined) => (value?.name ? value.name : value),
  },
  {
    field: 'rechnungsnummer',
    headerName: 'Rechnungsnummer',
    sortable: false,
    flex: 2,
  },
  {
    field: 'ticketId',
    headerName: 'Ticket Id',
    sortable: false,
    flex: 2,
  },
  {
    field: 'istFakturierbar',
    headerName: 'Fakturierbar',
    sortable: false,
    flex: 2,
    type: 'boolean',
  },
  {
    field: 'dauerInMillis',
    headerName: 'Dauer in Stunden',
    flex: 2,
    type: 'number',
    valueFormatter: value => ((value as number) / 3600000).toLocaleString('de'),
  },
  {
    field: 'datum',
    headerName: 'Datum',
    flex: 2,
    type: 'date',
    valueFormatter: value => formatDate(value),
  },
  {
    field: 'creationDate',
    headerName: 'Angelegt am',
    flex: 1,
    type: 'date',
    valueFormatter: value => formatDate(value),
  },
];

interface AufwandTableProps {
  initialFilterItems?: AufwandApiFilters;
  stackProps?: StackProps;
  gridHeight?: number | string;
}

interface SortState {
  [key: string]: 'asc' | 'desc';
}

export const AufwandTable: FC<AufwandTableProps> = ({
  initialFilterItems,
  stackProps,
  gridHeight = 600,
}) => {
  const mitarbeiterId = useAppSelector(selectMitarbeiterId);
  const mitarbeiterRole = useMitarbeiterRole();

  const [deleteAufwaendeDialogOpen, setDeleteAufwaendeDialogOpen] = useState(false);

  const navigateToEditAufwand = useNavigateDialogForm<Aufwand>('aufwand', 'edit');

  const navigateDialog = useNavigateDialogForm<Aufwand>(
    'aufwand',
    'create',
    initialFilterItems && initialFilterItems.projektId
      ? {
          mitarbeiterId: mitarbeiterId ?? '',
          projektId: initialFilterItems.projektId.value,
          datum: new Date().toISOString(),
        }
      : {
          mitarbeiterId: mitarbeiterId ?? '',
          datum: new Date().toISOString(),
        }
  );

  const columns = useMemo(() => {
    const defaultColumns = aufwandTableColumns.map(atc => ({ ...atc }));
    const actionColumn: GridActionsColDef = {
      field: 'edit',
      type: 'actions',
      getActions: params => {
        const aufwand = params.row as Aufwand;
        if (mitarbeiterRole === MitarbeiterRole.ADMIN || aufwand.mitarbeiterId === mitarbeiterId) {
          return [
            <GridActionsCellItem
              key="Edit"
              icon={<Edit />}
              label="Edit"
              onClick={() => navigateToEditAufwand(aufwand)}
            />,
          ];
        }
        return [];
      },
    };
    return [...defaultColumns, actionColumn];
  }, [mitarbeiterId, mitarbeiterRole, navigateToEditAufwand]);

  const [showSidePanel, setShowSidePanel] = useState(false);

  const [paginationState, setPaginationState] = useState<PaginationState>({
    page: 0,
    pageSize: 25,
  });

  const [sortState, setSortState] = useState<SortState>({ datum: 'desc' });

  const [apiFilters, setApiFilters] = useState<AufwandApiFilters | undefined>({
    datum: {
      operator: 'between',
      from: startOfDay(new Date()).toISOString(),
      to: endOfYear(new Date()).toISOString(),
    },
    ...initialFilterItems,
  });

  const { data, isFetching } = useFindAllAufwandQuery({
    filter: apiFilters,
    pagination: paginationState,
    sort: sortState,
  });

  const [showZusammenfassungDialog, setShowZusammenfassungDialog] = useState(false);
  const toggleZusammenfassungDialog = () => setShowZusammenfassungDialog(p => !p);

  const [selection, setSelection] = useState<Aufwand[] | null>(null);

  const aufwaende = data?.data ?? [];

  const selectedAufwaendeIds = selection?.map(a => a.id) ?? [];

  return (
    <>
      <AufwaendeZusammenfassungDialog
        aufwaendeIds={selection?.map(a => a.id) ?? []}
        open={showZusammenfassungDialog}
        onClose={toggleZusammenfassungDialog}
      />
      <DeleteAufandDialog
        open={deleteAufwaendeDialogOpen}
        aufwaendeIds={selectedAufwaendeIds}
        onClose={() => setDeleteAufwaendeDialogOpen(false)}
      />
      <Stack
        direction="row"
        spacing={2}
        {...stackProps}
        sx={{
          ...(stackProps?.sx as any),
          // Allow the grid container to actually shrink when the side panel grows
          minWidth: 0,
          overflowX: 'hidden',
        }}
      >
        <FilterSidePanel
          open={showSidePanel}
          toggleOpen={() => setShowSidePanel(prev => !prev)}
          apiFilters={apiFilters}
          setApiFilters={setApiFilters}
        />
        <Box sx={{ flexGrow: 1, minWidth: 0, overflowX: 'hidden' }}>
          <AppDataGrid
            gridHeight={gridHeight}
            showToolbar
            slots={{
              toolbar: () => (
                <Toolbar>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                    {!selection ? (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<Add />}
                        onClick={() => navigateDialog()}
                      >
                        Neuer Aufwand
                      </Button>
                    ) : (
                      <Stack direction="row" spacing={2}>
                        {mitarbeiterRole !== MitarbeiterRole.EXTERN && (
                          <Tooltip title="Eine Rechnungsnummer vergeben" placement="top">
                            {/* TODO make tooltip work */}
                            {/* see https://mui.com/material-ui/react-tooltip/*/}
                            <RechnungsNummerDialogButton selectedAufwaende={selection} />
                          </Tooltip>
                        )}
                        <Tooltip title="Eine Zusammenfassung erstellen" placement="top">
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={toggleZusammenfassungDialog}
                          >
                            Zusammenfassung
                          </Button>
                        </Tooltip>
                        <Tooltip title="Ein CSV exportieren" placement="top">
                          <ExportCsv
                            options={{
                              includeHeaders: true,
                              delimiter: ';',
                              fileName: 'EffortLog-Aufwände',
                              utf8WithBom: true,
                            }}
                            render={
                              <Button variant="contained" color="secondary">
                                Exportieren
                              </Button>
                            }
                          />
                        </Tooltip>
                        {mitarbeiterRole === MitarbeiterRole.ADMIN && (
                          <Tooltip title="Ausgewählte Einträge löschen" placement="top">
                            <Button
                              onClick={() => setDeleteAufwaendeDialogOpen(true)}
                              variant="contained"
                              color="secondary"
                            >
                              Löschen
                            </Button>
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Toolbar>
              ),
            }}
            filterMode="server"
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                  creationDate: false,
                  ...(initialFilterItems
                    ? Object.keys(initialFilterItems).reduce(
                        (acc, key) => {
                          acc[key] = false;
                          return acc;
                        },
                        {} as Record<string, boolean>
                      )
                    : {}),
                  datum: true,
                },
              },
              sorting: { sortModel: [{ field: 'datum', sort: 'desc' }] },
            }}
            disableColumnFilter={true}
            onCellClick={(params, event) => {
              // Ignore clicks on action cells (edit button)
              if (params.field === 'edit') {
                return;
              }

              event.preventDefault();
              event.stopPropagation();

              const rowId = params.id;
              const currentSelection = params.api.getSelectedRows();
              const isSelected = currentSelection.has(rowId);

              if (isSelected) {
                params.api.selectRow(rowId, false);
              } else {
                params.api.selectRow(rowId, true);
              }
            }}
            checkboxSelection={true}
            loading={isFetching}
            columns={columns}
            rows={aufwaende}
            pagination
            paginationMode="server"
            rowCount={data?.pagination.count ?? 0}
            paginationModel={paginationState}
            onPaginationModelChange={setPaginationState}
            pageSizeOptions={[25, 50, 100, 200, 1000, 3000]}
            sortingMode="server"
            onSortModelChange={model =>
              setSortState(
                model.reduce((acc, cur) => {
                  acc[cur.field] = cur.sort ? (_.upperCase(cur.sort) as 'asc' | 'desc') : 'asc';
                  return acc;
                }, {} as SortState)
              )
            }
            onRowSelectionModelChange={(ids, details) => {
              const aufwandIterator = details.api
                .getSelectedRows()
                .values() as IterableIterator<Aufwand>;
              const aufwandArray = Array.from(aufwandIterator);
              setSelection(aufwandArray.length === 0 ? null : aufwandArray);
            }}
          />
        </Box>
      </Stack>
    </>
  );
};
